/**
 * Devices list — Phase 1 (Phase 5 update: device_label is now optional).
 *
 * Columns (per Agent D §10.4):
 *   label-or-serial · serial · customer · channel · current_firmware_version ·
 *   last_seen_at (color-coded) · subscription_active (badge)
 *
 * Phase 5: flash-fleet.sh no longer requires `--device-label`. Devices that
 * boot without an operator-supplied label show their auto-derived serial
 * (`RC-XXXXXXXX`) in the Label column instead of an em-dash, so the row
 * is still self-describing.
 *
 * Filters:
 *   channel (free-text), customer (dropdown from /api/admin/customers),
 *   online (last_seen within 10 min — server-side filter).
 *
 * Filters are kept in component state and applied as `react-query` query
 * keys, so toggling them automatically refetches with the current cache
 * granularity. We deliberately don't push them to the URL in Phase 1; that's
 * a small follow-up if/when operators ask to share filter snapshots.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import { getFleetHealth, listCustomers, listDevices } from "@/lib/api";
import {
  formatDateTime,
  formatRelative,
  freshnessClasses,
  freshnessOf,
} from "@/lib/format";
import type { Customer, DevicesListFilter, FleetHealthCounts } from "@/lib/types";

export default function Devices() {
  const [filter, setFilter] = useState<DevicesListFilter>({});

  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: listCustomers,
  });

  const devicesQ = useQuery({
    queryKey: ["devices", filter],
    queryFn: () => listDevices(filter),
  });

  const fleetHealthQ = useQuery({
    queryKey: ["fleet-health"],
    queryFn: getFleetHealth,
    staleTime: 30_000,
  });

  const customersById = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of customersQ.data ?? []) m.set(c.id, c);
    return m;
  }, [customersQ.data]);

  const channels = useMemo(() => {
    const set = new Set<string>(["stable", "beta"]);
    for (const d of devicesQ.data ?? []) set.add(d.channel);
    return Array.from(set).sort();
  }, [devicesQ.data]);

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle={
          devicesQ.data
            ? `${devicesQ.data.length} device${devicesQ.data.length === 1 ? "" : "s"} in fleet`
            : "Loading fleet…"
        }
      />

      <div className="px-8 py-6 space-y-4">
        <FleetHealthCard
          counts={fleetHealthQ.data}
          loading={fleetHealthQ.isLoading}
          error={fleetHealthQ.isError}
          activeHealth={filter.health}
          onPill={(h) => {
            if (h === "total") {
              setFilter({});
            } else {
              setFilter({ health: h });
            }
          }}
        />
        <FiltersBar
          filter={filter}
          onChange={setFilter}
          channels={channels}
          customers={customersQ.data ?? []}
          healthFilter={filter.health}
        />

        <div className="rounded-md border border-surface-border bg-surface overflow-hidden">
          {devicesQ.isLoading ? (
            <LoadingBlock label="Loading devices…" />
          ) : devicesQ.isError ? (
            <ErrorBlock error={devicesQ.error} onRetry={() => devicesQ.refetch()} />
          ) : !devicesQ.data || devicesQ.data.length === 0 ? (
            <EmptyBlock
              title="No devices match these filters."
              description="Adjust the filters above, or seed a device with backend/db/seed.sql."
            />
          ) : (
            <DevicesTable
              devices={devicesQ.data}
              customersById={customersById}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fleet health
// ---------------------------------------------------------------------------

function FleetHealthCard({
  counts,
  loading,
  error,
  activeHealth,
  onPill,
}: {
  counts: FleetHealthCounts | undefined;
  loading: boolean;
  error: boolean;
  activeHealth?: DevicesListFilter["health"];
  onPill: (h: "total" | "online" | "stale" | "offline") => void;
}) {
  const pill = (
    key: "total" | "online" | "stale" | "offline",
    label: string,
    value: number | string,
    tone: string,
    ring: string,
  ) => {
    const active =
      (key === "total" && !activeHealth) ||
      (key !== "total" && activeHealth === key);
    return (
      <button
        type="button"
        key={key}
        onClick={() => onPill(key)}
        className={[
          "inline-flex flex-col items-start rounded-lg border px-4 py-2 text-left transition-colors min-w-[100px]",
          tone,
          active ? ring : "ring-1 ring-inset ring-surface-border opacity-90",
        ].join(" ")}
      >
        <span className="text-[10px] uppercase tracking-wider text-brand-dim">
          {label}
        </span>
        <span className="text-xl font-semibold tabular-nums text-slate-50">
          {value}
        </span>
      </button>
    );
  };

  return (
    <div className="rounded-md border border-surface-border bg-surface px-4 py-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-slate-100 tracking-wide">
          Fleet health
        </h2>
        {error ? (
          <span className="text-xs text-rose-300">Could not load counts</span>
        ) : loading ? (
          <span className="text-xs text-brand-dim">Updating…</span>
        ) : (
          <span className="text-xs text-brand-dim">Refreshes every 30s</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {pill(
          "total",
          "Total",
          counts?.total ?? "—",
          "bg-surface-sunken/80 border-surface-border",
          "ring-2 ring-brand-orange/50",
        )}
        {pill(
          "online",
          "Online",
          counts?.online ?? "—",
          "bg-emerald-500/10 border-emerald-500/30",
          "ring-2 ring-emerald-400/60",
        )}
        {pill(
          "stale",
          "Stale",
          counts?.stale ?? "—",
          "bg-amber-500/10 border-amber-500/35",
          "ring-2 ring-amber-400/55",
        )}
        {pill(
          "offline",
          "Offline",
          counts?.offline ?? "—",
          "bg-rose-500/10 border-rose-500/35",
          "ring-2 ring-rose-400/55",
        )}
      </div>
      <p className="mt-2 text-[11px] text-brand-dim">
        Online = last seen &lt; 10 min · Stale = 10 min–1 h · Offline = ≥ 1 h or never
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters bar
// ---------------------------------------------------------------------------

function FiltersBar({
  filter,
  onChange,
  channels,
  customers,
  healthFilter,
}: {
  filter: DevicesListFilter;
  onChange: (next: DevicesListFilter) => void;
  channels: string[];
  customers: Customer[];
  healthFilter?: DevicesListFilter["health"];
}) {
  return (
    <div className="flex flex-wrap items-end gap-4 rounded-md border border-surface-border bg-surface px-4 py-3">
      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase tracking-wider text-brand-dim">
          Channel
        </label>
        <select
          value={filter.channel ?? ""}
          onChange={(e) =>
            onChange({ ...filter, channel: e.target.value || undefined })
          }
          className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
        >
          <option value="">All</option>
          {channels.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-[11px] uppercase tracking-wider text-brand-dim">
          Customer
        </label>
        <select
          value={filter.customer ?? ""}
          onChange={(e) =>
            onChange({ ...filter, customer: e.target.value || undefined })
          }
          className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
        >
          <option value="">All</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {healthFilter ? (
        <span className="text-xs text-brand-teal">
          Health filter: <span className="font-mono">{healthFilter}</span>
        </span>
      ) : null}

      {(filter.channel || filter.customer || filter.health) && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="ml-auto text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Devices table
// ---------------------------------------------------------------------------

function DevicesTable({
  devices,
  customersById,
}: {
  devices: import("@/lib/types").Device[];
  customersById: Map<string, Customer>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
          <tr>
            <th className="px-4 py-2 font-medium">Label</th>
            <th className="px-4 py-2 font-medium">Serial</th>
            <th className="px-4 py-2 font-medium">Customer</th>
            <th className="px-4 py-2 font-medium">Channel</th>
            <th className="px-4 py-2 font-medium">Firmware</th>
            <th className="px-4 py-2 font-medium">Last seen</th>
            <th className="px-4 py-2 font-medium">Subscription</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {devices.map((d) => {
            const customer = d.customer_id
              ? customersById.get(d.customer_id)
              : null;
            const tone = freshnessOf(d.last_seen_at);
            return (
              <tr
                key={d.serial}
                className="hover:bg-surface-raised/60 transition-colors"
              >
                <td className="px-4 py-2 text-slate-100">
                  <Link
                    to={`/devices/${encodeURIComponent(d.serial)}`}
                    className="text-brand-orange hover:underline"
                  >
                    {d.device_label ?? d.serial}
                  </Link>
                  {d.device_label ? null : (
                    <span
                      className="ml-2 rounded-full bg-surface-raised px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-brand-dim"
                      title="No operator label was set at flash time; the row is keyed on the auto-derived serial."
                    >
                      auto
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-slate-300">
                  {d.serial}
                </td>
                <td className="px-4 py-2 text-slate-200">
                  {customer ? customer.name : <span className="text-brand-dim">unassigned</span>}
                </td>
                <td className="px-4 py-2">
                  <ChannelChip channel={d.channel} pinned={d.pinned_version} />
                </td>
                <td className="px-4 py-2 font-mono text-xs text-slate-300">
                  {d.current_firmware_version ?? "—"}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-xs ${freshnessClasses(tone)}`}
                    title={d.last_seen_at ? formatDateTime(d.last_seen_at) : "never"}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        tone === "green"
                          ? "bg-emerald-400"
                          : tone === "yellow"
                            ? "bg-amber-400"
                            : "bg-rose-400"
                      }`}
                    />
                    {formatRelative(d.last_seen_at)}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <SubscriptionBadge active={d.subscription_active} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ChannelChip({
  channel,
  pinned,
}: {
  channel: string;
  pinned: string | null;
}) {
  if (pinned) {
    return (
      <span
        title={`Pinned to ${pinned} (overrides channel ${channel})`}
        className="inline-flex items-center gap-1 rounded-full bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40 px-2 py-0.5 text-xs font-mono"
      >
        pin · {pinned}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs">
      {channel}
    </span>
  );
}

function SubscriptionBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30 px-2 py-0.5 text-xs">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30 px-2 py-0.5 text-xs">
      Inactive
    </span>
  );
}
